import React, { useMemo } from 'react';
import type { Chef, Language, UserMealPreferences } from '../types';
import ChefAvatar from './ChefAvatar';
import XIcon from './icons/XIcon';
import HeartIcon from './icons/HeartIcon';
import GlobeIcon from './icons/GlobeIcon';
import InstagramIcon from './icons/InstagramIcon';
import FacebookIcon from './icons/FacebookIcon';
import ChefHatIcon from './icons/ChefHatIcon';
import { getTranslation } from '../utils/translations';
import { getCountryDisplayName } from '../utils/countryDisplay';
import { getChefDisplayName, getChefWebsites, normalizeWebsiteUrl, openExternalLink } from '../utils/chefDisplay';
import { isFacebookUrl } from '../utils/facebookDisplay';
import { isFavoriteChef } from '../utils/userMealPreferences';

interface ChefProfileCardProps {
    chef: Chef;
    recipeCount: number;
    language?: Language;
    userMealPreferences?: UserMealPreferences | null;
    onToggleFavoriteChef?: (chefId: string) => Promise<void>;
    onViewRecipes?: (chefId: string) => void;
    onEdit?: () => void;
    onClose?: () => void;
}

const INSTAGRAM_HOST_RE = /(^|\.)instagram\.com/i;

function isInstagramUrl(url: string): boolean {
    try {
        return INSTAGRAM_HOST_RE.test(new URL(url).hostname);
    } catch {
        return INSTAGRAM_HOST_RE.test(url);
    }
}

function isFacebookLink(url: string): boolean {
    const href = normalizeWebsiteUrl(url) ?? url;
    return isFacebookUrl(href);
}

const ChefProfileCard: React.FC<ChefProfileCardProps> = ({
    chef,
    recipeCount,
    language = 'en',
    userMealPreferences = null,
    onToggleFavoriteChef,
    onViewRecipes,
    onEdit,
    onClose,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const displayName = getChefDisplayName(chef, language);

    const { websiteLink, instagramLink, facebookLink } = useMemo(() => {
        const links = getChefWebsites(chef);

        const instagramFromLinks = links.find((raw) => {
            const href = normalizeWebsiteUrl(raw);
            const candidate = href ?? raw;
            return isInstagramUrl(candidate);
        });
        const facebookFromLinks = links.find((raw) => {
            const href = normalizeWebsiteUrl(raw);
            const candidate = href ?? raw;
            return isFacebookLink(candidate);
        });
        const websiteFromLinks = links.find((raw) => {
            const href = normalizeWebsiteUrl(raw);
            const candidate = href ?? raw;
            return !isInstagramUrl(candidate) && !isFacebookLink(candidate);
        });

        const instagramFromUsername = chef.instagramUsername?.trim()
            ? `instagram.com/${chef.instagramUsername.trim().replace(/^@/, '')}`
            : null;

        return {
            websiteLink: websiteFromLinks ?? null,
            instagramLink: instagramFromLinks ?? instagramFromUsername,
            facebookLink: facebookFromLinks ?? null,
        };
    }, [chef]);

    const handleOpenLink = (raw: string) => {
        openExternalLink(raw);
    };

    const alternateName =
        chef.name && chef.englishName && chef.name !== chef.englishName && language !== chef.originLanguage
            ? chef.name
            : null;

    const hasSocialLinks = !!(websiteLink || instagramLink || facebookLink);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-3xl overflow-hidden shadow-lg w-full max-w-sm self-center border border-slate-100 dark:border-slate-800">
            <div className="relative h-24 bg-gradient-to-b from-brand-sage/15 to-brand-sage/5 dark:from-brand-sage/30 dark:to-slate-800/80">
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-3 end-3 p-2 bg-white/90 dark:bg-slate-900/90 rounded-full text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 shadow-md transition-colors"
                        aria-label={t('close')}
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                )}
                {onToggleFavoriteChef && (
                    <button
                        type="button"
                        onClick={() => onToggleFavoriteChef(chef.id)}
                        className={`absolute top-3 start-3 p-2 bg-white/90 dark:bg-slate-900/90 rounded-full shadow-md transition-colors ${
                            isFavoriteChef(userMealPreferences, chef.id)
                                ? 'text-rose-500 hover:text-rose-600'
                                : 'text-slate-400 hover:text-rose-400'
                        }`}
                        title={isFavoriteChef(userMealPreferences, chef.id) ? t('unfavorite') : t('favorite')}
                    >
                        <HeartIcon className="w-5 h-5" filled={isFavoriteChef(userMealPreferences, chef.id)} />
                    </button>
                )}
            </div>

            <div className="flex justify-center -mt-14 relative z-10 px-6">
                <ChefAvatar
                    imageUrl={chef.imageUrl}
                    name={displayName}
                    size="xl"
                    className="border-4 border-white dark:border-slate-900 shadow-sm"
                />
            </div>

            <div className="flex flex-col items-center px-6 pt-3 pb-6">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1 text-center font-nunito">
                    {displayName}
                </h2>

                {alternateName && (
                    <p className="text-sm text-slate-400 dark:text-slate-500 mb-1 text-center">{alternateName}</p>
                )}

                {chef.description?.trim() && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-2 leading-relaxed max-w-xs">
                        {chef.description.trim()}
                    </p>
                )}

                {chef.originCountry && (
                    <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide bg-slate-50 dark:bg-slate-800 px-3 py-1 rounded-full mb-4">
                        {getCountryDisplayName(chef.originCountry, language)}
                    </span>
                )}

                {hasSocialLinks && (
                    <div className="flex flex-row justify-center gap-4 mb-6 w-full">
                        {websiteLink && (
                            <button
                                type="button"
                                onClick={() => handleOpenLink(websiteLink)}
                                className="w-11 h-11 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-950/60 transition-colors active:scale-95"
                                aria-label={t('chefLinks')}
                            >
                                <GlobeIcon className="w-5 h-5" />
                            </button>
                        )}
                        {facebookLink && (
                            <button
                                type="button"
                                onClick={() => handleOpenLink(facebookLink)}
                                className="w-11 h-11 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center text-[#1877F2] hover:bg-blue-100 dark:hover:bg-blue-950/60 transition-colors active:scale-95"
                                aria-label="Facebook"
                            >
                                <FacebookIcon className="w-5 h-5" />
                            </button>
                        )}
                        {instagramLink && (
                            <button
                                type="button"
                                onClick={() => handleOpenLink(instagramLink)}
                                className="w-11 h-11 rounded-full bg-pink-50 dark:bg-pink-950/40 flex items-center justify-center text-pink-500 hover:bg-pink-100 dark:hover:bg-pink-950/60 transition-colors active:scale-95"
                                aria-label="Instagram"
                            >
                                <InstagramIcon className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                )}

                {recipeCount > 0 && onViewRecipes && (
                    <button
                        type="button"
                        onClick={() => onViewRecipes(chef.id)}
                        className="bg-brand-sage text-white w-full py-3.5 rounded-xl flex flex-row items-center justify-center gap-2 shadow-sm hover:opacity-90 active:opacity-80 transition-opacity font-black text-sm"
                    >
                        <ChefHatIcon className="w-5 h-5 shrink-0" />
                        <span>{t('viewChefRecipes').replace('{count}', String(recipeCount))}</span>
                    </button>
                )}

                {onEdit && (
                    <button
                        type="button"
                        onClick={onEdit}
                        className="mt-3 w-full py-3 border border-brand-sage/30 text-brand-sage font-black rounded-xl text-xs uppercase tracking-widest hover:bg-brand-sage/5 transition-colors"
                    >
                        {t('editChef')}
                    </button>
                )}
            </div>
        </div>
    );
};

export default ChefProfileCard;
